import React, { FormEvent, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import * as yup from 'yup'
import cn from 'classnames'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'

import Text from '../../../ui/Text'
import { SettingName } from '../../../userapp/components/user-profile-language-select'
import Claims from '../../../lib/claims'
import { isAdminDomain, isPartnerDomain, isSupportDomain, isUserappDomain } from '../../../lib/currentDomain'
import currentUser, { CurrentUser } from '../../../lib/currentUser'
import { FormHeader } from '../../../lib/form-header'
import { InputGroup, SInput } from '../../../lib/input-group'
import { SContentWrapper, SNotification, SSpacer } from '../../../lib/landing-page-layout'
import { PasswordInput } from '../../../lib/password-input'
import storage from '../../../common/utils/storage'
import { Button } from '../../../common/components/Button'
import {
    useAuthLoginMutation,
    useAuthVerifyEmailMutation,
    useLazyCompanyGetAdministratedCompaniesQuery,
    useLazyUserGetUserSettingsQuery,
    useLazyUserIsUserHasChargePointsQuery,
    useUserUpdateUserSettingsMutation,
} from '../../../common/redux/api/web-api'
import { notifyError } from '../../../common/features/notificationsManager/Notify'
import getApiToken from '../../../lib/getApiToken'
import { triggerAuthChange } from '../../../common/utils/broadcastChannel'
import { setToken } from '../../redux/features/token/tokenSlice'
import { setUser } from '../../redux/features/user/userSlice'
import useText from '../../hooks/useText'
import { useLocalizationContext } from '../localization-provider'

const SRow = styled.div`
    display: flex;
    justify-content: space-between;
`

const SSignUpButton = styled.div`
    font-weight: 400;
    &:hover {
        cursor: pointer;
    }
`

const SForgotPassword = styled.div`
    margin-left: auto;
`

const SForgotPasswordLink = styled.span`
    color: #000;
    cursor: pointer;
    font-weight: 200;
`

export function formatValidationErrorData<T>(errors: yup.ValidationError, initialErrorData: T): T {
    let errorData = initialErrorData
    for (const err of errors.inner) {
        if (err.path) {
            errorData = {
                ...errorData,
                [err.path]: err.message,
            }
        }
    }

    return errorData
}

const initData = {
    email: '',
    password: '',
    hostName: window.location.hostname,
}

export type INotification = { text: string; type?: string } | null

export function SignInForm() {
    const text = useText()
    const dispatch = useDispatch()
    const routerLocation = useLocation()
    const { locale, setLocale } = useLocalizationContext()
    const from = routerLocation?.state?.from || '/'
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [submitError, setSubmitError] = useState('')
    const [showPassword, toggleShowPassword] = useState(false)
    const [formData, setFormData] = useState<Credentials>(() => initData)
    const [formErrors, setFormErrors] = useState(() => initData)
    const [notification, setNotification] = useState(storage.getItem<INotification>('notification'))

    useEffect(() => {
        storage.removeItem('notification')
    }, [])

    const credentialsSchema = yup.object().shape({
        email: yup.string().email(text('msgEmailFormatIsIncorrect')).required(text('msgEmailIsRequired')),
        password: yup.string().required(text('msgPasswordRequiredsignin')),
    })

    const showForgotPassword = !isSupportDomain

    const [verifyEmail] = useAuthVerifyEmailMutation()

    useEffect(() => {
        const token = searchParams?.get('token')
        if (token) {
            verifyEmail({ token })
                .unwrap()
                .then(() => {
                    setNotification({ text: 'msgEmailVerified', type: 'notice' })
                })
                .catch(() => {
                    setNotification({ text: 'msgEmailVerifiedFailed', type: 'alert' })
                })
        }
    }, [searchParams])

    /******/

    /**
     * Get chargepoints
     */
    const [getUserHasChargePoints, { isLoading: chargePointIsLoading }] = useLazyUserIsUserHasChargePointsQuery()

    const checkChargePoints = async () => {
        getUserHasChargePoints()
            .unwrap()
            .then((hasChargePoints) => {
                if (hasChargePoints) {
                    navigate(from, { replace: true })
                } else {
                    navigate('/registerchargepoint', { replace: true })
                }
            })
            .catch(() => {
                setNotification({ text: 'msgPleaseTryLoginLater', type: 'alert' })
                storage.removeItem('token')
                storage.removeItem('user')
                currentUser(null)
            })
    }

    /**
     * Sign in request
     */
    const [login, { isLoading: loginIsLoading }] = useAuthLoginMutation()

    const loading = loginIsLoading || chargePointIsLoading

    const [getAdminCompanies] = useLazyCompanyGetAdministratedCompaniesQuery()
    const [getUserSettings] = useLazyUserGetUserSettingsQuery()
    const [saveUserSettings] = useUserUpdateUserSettingsMutation()

    const checkForAvailableOrganisations = async (user: CurrentUser) => {
        try {
            const { data: organisations, isError, error } = await getAdminCompanies(undefined)

            if (isError) throw error

            // handle logged in scenario
            if (user.selectedCompanyId) {
                navigate(from, { replace: true })
                return
            }
            if (organisations?.length === 1) {
                currentUser({ ...user, selectedCompanyId: organisations[0].id })
                dispatch(setUser({ ...user, selectedCompanyId: organisations[0].id }))
                navigate(from, { replace: true })
                return
            }
            navigate('/select-organisation', { replace: true, state: { from } })
        } catch (exception: unknown) {
            notifyError(exception.toString())
        }
    }

    const handleLogin = async ({ token, user }: { token?: string; user?: CurrentUser }) => {
        if (token && user) {
            if (
                isAdminDomain &&
                !user.isSuperAdmin &&
                !user.roles.some((r) => r.claim === Claims.Admin || r.claim === Claims.AdminViewOnly)
            ) {
                setSubmitError(text('msgDeniedForNonAdministrators'))
                return
            }
            if (isPartnerDomain && !(user.isPartner && user.partnerId)) {
                setSubmitError(text('msgDeniedForNonPartners'))
                return
            }
            if (isSupportDomain && !user.isSuperAdmin) {
                setSubmitError(text('msgAccessDeniedMustBeAdmin'))
                return
            }

            storage.setItem('token', token)
            dispatch(setToken(token))
            try {
                const { data: settings } = await getUserSettings({ userId: user.id })

                if (settings) {
                    const savedLanguage = settings.find((setting) => setting.name === SettingName.LANGUAGE)?.value

                    // if there is no saved language
                    if (!savedLanguage || locale !== savedLanguage) {
                        await saveUserSettings({
                            userId: user.id,
                            body: [
                                {
                                    name: SettingName.LANGUAGE,
                                    value: locale,
                                },
                            ],
                        }).unwrap()
                    } else if (savedLanguage) {
                        setLocale(savedLanguage)
                    }

                    if (isUserappDomain) {
                        const isGreenChargingInformed = settings.find(
                            (setting) => setting.name === SettingName.GREEN_CHARGING_INFORMED
                        )
                        if (!isGreenChargingInformed) {
                            storage.setItem('showGreenChargingModal', 'true')
                        }
                    }
                }
            } catch (error) {
                notifyError(`Cannot load user settings: ${error}`)
            }

            currentUser(user)
            dispatch(setUser(user))

            if (isUserappDomain) {
                if (!user.personalAccept && !user.companyAccept) navigate('/user-agreement', { replace: true })
                else checkChargePoints()
            } else if (isAdminDomain) {
                checkForAvailableOrganisations(user)
            } else if (isPartnerDomain) {
                navigate(user.isPartner ? from : '/registerPartner', { replace: true })
                return
            } else {
                navigate(from, { replace: true })
            }
        }
    }

    useEffect(() => {
        const token = getApiToken()
        const user = currentUser()
        if (token && user) {
            handleLogin({ token, user })
        }
    }, [])

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()
        setNotification(null)
        setSubmitError('')

        credentialsSchema
            .validate(formData, { abortEarly: false })
            .then(() => {
                setFormErrors(() => initData)
                login({ userCredentials: formData })
                    .unwrap()
                    // TODO: check swagger doc response typing
                    .then((res) => {
                        handleLogin(res as { token?: string; user?: CurrentUser })
                        triggerAuthChange()
                    })
                    .catch(() => {
                        setSubmitError(text('msgWrongCredentialsError'))
                    })
            })
            .catch((error) => {
                setFormErrors(() => formatValidationErrorData<Credentials>(error, initData) || initData)
            })
    }

    const changeFormData = (value: string, parameterName: string) => {
        setFormData((prev) => ({
            ...prev,
            [parameterName]: value,
        }))
        setFormErrors((prev) => ({
            ...prev,
            [parameterName]: '',
        }))
    }

    return (
        <SContentWrapper>
            {submitError && (
                <SSpacer>
                    <SNotification data-testid="signInError" error={submitError !== ''}>
                        {submitError}
                    </SNotification>
                </SSpacer>
            )}

            {notification && (
                <SSpacer>
                    <SNotification error={notification?.type === 'alert'} data-testid="notification">
                        {text(notification.text)}
                    </SNotification>
                </SSpacer>
            )}

            <SSpacer>
                <FormHeader inverted={isSupportDomain} data-testid="header" />
            </SSpacer>

            <form onSubmit={handleSubmit} noValidate>
                <SSpacer>
                    <InputGroup
                        label={formData.email.length > 0 ? text('lblEmail') : ''}
                        errorTestId="signInEmailErrorMsg"
                        errorMsg={formErrors.email}
                        inverted={isSupportDomain}
                    >
                        <SInput
                            inverted={isSupportDomain}
                            data-testid="emailInput"
                            type="email"
                            placeholder={text('lblEmail')}
                            onInput={(e) => {
                                /** onChange is not triggered by autofill on chrome
                                 * onInput is though
                                 */
                                const value = (e.target as HTMLInputElement).value
                                changeFormData(value, 'email')
                            }}
                            autoFocus
                            formNoValidate
                        />
                    </InputGroup>
                </SSpacer>

                <SSpacer>
                    <PasswordInput
                        label={formData.password.length > 0 ? text('lblPassword') : ''}
                        placeholder={text('lblPassword')}
                        onChange={(value: string) => changeFormData(value, 'password')}
                        showPassword={showPassword}
                        error={formErrors.password}
                        errorTestId="signInPasswordErrorMsg"
                        toggleShowPasswordFlag={toggleShowPassword}
                        hasText={formData.password.length > 0}
                    />
                </SSpacer>

                <SSpacer>
                    <Button
                        loading={loading}
                        inverted={isSupportDomain}
                        label={text('lblSignIn')}
                        className={cn('sign-in-btn', 'w-full')}
                        data-testid="submitButton"
                        type="submit"
                        arrow
                    />
                </SSpacer>
                <SRow>
                    {(isUserappDomain || isPartnerDomain) && (
                        <SSignUpButton
                            onClick={() =>
                                navigate(isPartnerDomain ? '/register-partner' : '/register-user', { replace: true })
                            }
                            data-testid="signUpBtn"
                        >
                            <Text code="lblSignUp" />
                        </SSignUpButton>
                    )}
                    {showForgotPassword && (
                        <SForgotPassword>
                            <SForgotPasswordLink
                                onClick={() => navigate(`/reset-password-request`, { replace: true })}
                                data-testid="forgotPasswordBtn"
                            >
                                <Text code="lblForgotPassword" />
                            </SForgotPasswordLink>
                        </SForgotPassword>
                    )}
                </SRow>
            </form>
        </SContentWrapper>
    )
}

type Credentials = {
    email: string
    password: string
    hostName: string
}
